import { gql } from '@apollo/client';

// ============ Auth Queries ============

export const ME_QUERY = gql`
  query Me {
    me {
      id
      email
      publicKey
      encryptedPrivateKey
      privateKeyNonce
      createdAt
      updatedAt
      lastLoginAt
    }
  }
`;

export const GET_KDF_PARAMS = gql`
  query GetKDFParams($email: String!) {
    getKDFParams(email: $email) {
      algorithm
      salt
      N
      r
      p
      m
      t
      parallelism
    }
  }
`;

export const GET_USER_PUBLIC_KEY_QUERY = gql`
  query GetUserPublicKey($email: String!) {
    getUserPublicKey(email: $email)
  }
`;

// ============ Auth Mutations ============

export const REQUEST_SIGNUP_EMAIL_CODE_MUTATION = gql`
  mutation RequestSignupEmailCode($email: String!, $turnstileToken: String!) {
    requestSignupEmailCode(email: $email, turnstileToken: $turnstileToken) {
      success
      message
      retryAfter
    }
  }
`;

export const LOGIN_MUTATION = gql`
  mutation Login($input: LoginInput!) {
    login(input: $input) {
      success
      message
      requiresDeviceVerification
      challengeId
      user {
        id
        email
        publicKey
        encryptedPrivateKey
        privateKeyNonce
        createdAt
        updatedAt
      }
      kdfParams {
        algorithm
        salt
        N
        r
        p
        m
        t
        parallelism
      }
      _accessToken
      _refreshToken
    }
  }
`;

export const SEND_DEVICE_VERIFICATION_CODE_MUTATION = gql`
  mutation SendDeviceVerificationCode($challengeId: String!) {
    sendDeviceVerificationCode(challengeId: $challengeId) {
      success
      message
      retryAfter
    }
  }
`;

export const VERIFY_DEVICE_CODE_MUTATION = gql`
  mutation VerifyDeviceCode($input: VerifyDeviceInput!) {
    verifyDeviceCode(input: $input) {
      success
      message
      remainingAttempts
      user {
        id
        email
        publicKey
        encryptedPrivateKey
        privateKeyNonce
        createdAt
        updatedAt
      }
      kdfParams {
        algorithm
        salt
        N
        r
        p
        m
        t
        parallelism
      }
      _accessToken
      _refreshToken
    }
  }
`;

export const DEVICES_QUERY = gql`
  query Devices {
    devices {
      id
      deviceId
      label
      userAgent
      firstSeenAt
      lastSeenAt
      verifiedAt
      isCurrent
    }
  }
`;

export const REVOKE_DEVICE_MUTATION = gql`
  mutation RevokeDevice($deviceId: String!) {
    revokeDevice(deviceId: $deviceId) {
      success
      message
    }
  }
`;

export const SIGNUP_MUTATION = gql`
  mutation Signup($input: SignupInput!) {
    signup(input: $input) {
      success
      message
      user {
        id
        email
        publicKey
        createdAt
        updatedAt
      }
      _accessToken
      _refreshToken
    }
  }
`;

export const LOGOUT_MUTATION = gql`
  mutation Logout {
    logout {
      success
      message
    }
  }
`;

export const REFRESH_TOKEN_MUTATION = gql`
  mutation RefreshToken($token: String!) {
    refreshToken(token: $token) {
      success
      _accessToken
      _refreshToken
    }
  }
`;

// ============ Vault Queries ============

export const VAULT_ITEMS_QUERY = gql`
  query VaultItems($workspaceId: ID!, $type: VaultItemType) {
    vaultItems(workspaceId: $workspaceId, type: $type) {
      id
      type
      encryptedPayload
      payloadNonce
      itemKeyEncrypted
      itemKeyNonce
      isFavorite
      createdAt
      updatedAt
      isOwner
      ownerId
      ownerEmail
      ownerPublicKey
      owner {
        id
        email
        publicKey
      }
      shares {
        id
        itemId
        recipientId
        recipientEmail
        status
        sharedAt
        acceptedAt
      }
    }
  }
`;

export const VAULT_ITEM_QUERY = gql`
  query VaultItem($id: ID!, $workspaceId: ID!) {
    vaultItem(id: $id, workspaceId: $workspaceId) {
      id
      type
      encryptedPayload
      payloadNonce
      itemKeyEncrypted
      itemKeyNonce
      isFavorite
      createdAt
      updatedAt
      isOwner
      ownerId
      ownerEmail
      ownerPublicKey
      owner {
        id
        email
        publicKey
      }
      shares {
        id
        itemId
        recipientId
        recipientEmail
        status
        sharedAt
        acceptedAt
      }
    }
  }
`;

// ============ Vault Mutations ============

export const CREATE_VAULT_ITEM_MUTATION = gql`
  mutation CreateVaultItem($input: CreateVaultItemInput!) {
    createVaultItem(input: $input) {
      id
      type
      encryptedPayload
      payloadNonce
      itemKeyEncrypted
      itemKeyNonce
      isFavorite
      createdAt
      updatedAt
      isOwner
    }
  }
`;

export const UPDATE_VAULT_ITEM_MUTATION = gql`
  mutation UpdateVaultItem($input: UpdateVaultItemInput!) {
    updateVaultItem(input: $input) {
      id
      type
      encryptedPayload
      payloadNonce
      isFavorite
      updatedAt
    }
  }
`;

export const DELETE_VAULT_ITEM_MUTATION = gql`
  mutation DeleteVaultItem($id: ID!) {
    deleteVaultItem(id: $id) {
      success
      message
    }
  }
`;

// ============ Sharing Queries ============

export const SENT_SHARES_QUERY = gql`
  query SentShares($workspaceId: ID!) {
    sentShares(workspaceId: $workspaceId) {
      id
      itemId
      recipientId
      recipientEmail
      status
      sharedAt
      acceptedAt
    }
  }
`;

export const RECEIVED_SHARES_QUERY = gql`
  query ReceivedShares($workspaceId: ID!) {
    receivedShares(workspaceId: $workspaceId) {
      id
      itemId
      senderEmail
      senderPublicKey
      item {
        id
        type
        encryptedPayload
        payloadNonce
        itemKeyEncrypted
        itemKeyNonce
        isFavorite
        createdAt
        updatedAt
        isOwner
        ownerId
        ownerEmail
        ownerPublicKey
        owner {
          id
          email
          publicKey
        }
        shares {
          id
          recipientEmail
          status
        }
      }
      itemKeyEncrypted
      itemKeyNonce
      sharedAt
      acceptedAt
    }
  }
`;

export const PENDING_SHARES_QUERY = gql`
  query PendingShares($workspaceId: ID!) {
    pendingShares(workspaceId: $workspaceId) {
      id
      itemId
      senderEmail
      item {
        id
        type
        encryptedPayload
        payloadNonce
        itemKeyEncrypted
        itemKeyNonce
        isFavorite
        createdAt
        updatedAt
        isOwner
      }
      itemKeyEncrypted
      itemKeyNonce
      sharedAt
    }
  }
`;

// ============ Sharing Mutations ============

export const SHARE_VAULT_ITEM_MUTATION = gql`
  mutation ShareVaultItem($input: ShareVaultItemInput!) {
    shareVaultItem(input: $input) {
      success
      message
    }
  }
`;

export const ACCEPT_DIRECT_SHARE_MUTATION = gql`
  mutation AcceptDirectShare($input: AcceptDirectShareInput!) {
    acceptDirectShare(input: $input) {
      success
      message
    }
  }
`;

export const REVOKE_SHARE_MUTATION = gql`
  mutation RevokeShare($input: RevokeShareInput!) {
    revokeShare(input: $input) {
      success
      message
    }
  }
`;

// ============ Members Queries ============

export const MEMBERS_QUERY = gql`
  query Members {
    members {
      id
      email
      role
      status
    }
  }
`;

export const GET_MEMBER_INVITE_BY_TOKEN_QUERY = gql`
  query GetMemberInviteByToken($token: String!) {
    getMemberInviteByToken(token: $token) {
      email
      role
      status
      expiresAt
    }
  }
`;

// ============ Members Mutations ============

export const INVITE_MEMBER_MUTATION = gql`
  mutation InviteMember($input: InviteMemberInput!) {
    inviteMember(input: $input) {
      success
      message
      member {
        id
        email
        role
        status
      }
      token
      invitePath
      inviteLink
    }
  }
`;

export const ACCEPT_MEMBER_INVITE_MUTATION = gql`
  mutation AcceptMemberInvite($input: AcceptMemberInviteInput!) {
    acceptMemberInvite(input: $input) {
      success
      message
    }
  }
`;

export const DELETE_MEMBER_MUTATION = gql`
  mutation DeleteMember($input: DeleteMemberInput!) {
    deleteMember(input: $input) {
      success
      message
    }
  }
`;

export const DELETE_ALL_MEMBERS_MUTATION = gql`
  mutation DeleteAllMembers {
    deleteAllMembers {
      success
      message
    }
  }
`;

// ============ Workspace Queries ============

export const MY_WORKSPACES_QUERY = gql`
  query MyWorkspaces {
    myWorkspaces {
      id
      name
      ownerUserId
      createdAt
      updatedAt
      myRole
      memberCount
    }
  }
`;

export const WORKSPACE_QUERY = gql`
  query Workspace($id: ID!) {
    workspace(id: $id) {
      id
      name
      ownerUserId
      createdAt
      updatedAt
      myRole
      memberCount
    }
  }
`;

export const WORKSPACE_MEMBERS_QUERY = gql`
  query WorkspaceMembers($workspaceId: ID!) {
    workspaceMembers(workspaceId: $workspaceId) {
      id
      workspaceId
      userId
      email
      publicKey
      role
      joinedAt
    }
  }
`;

export const WORKSPACE_SHARE_CANDIDATES_QUERY = gql`
  query WorkspaceShareCandidates($workspaceId: ID!) {
    workspaceShareCandidates(workspaceId: $workspaceId) {
      userId
      email
      publicKey
    }
  }
`;

export const WORKSPACE_INVITATIONS_QUERY = gql`
  query WorkspaceInvitations($workspaceId: ID!) {
    workspaceInvitations(workspaceId: $workspaceId) {
      id
      workspaceId
      workspaceName
      invitedEmail
      invitedByEmail
      status
      expiresAt
      createdAt
    }
  }
`;

export const GET_WORKSPACE_INVITE_BY_TOKEN_QUERY = gql`
  query GetWorkspaceInviteByToken($token: String!) {
    getWorkspaceInviteByToken(token: $token) {
      id
      workspaceId
      workspaceName
      invitedEmail
      invitedByEmail
      status
      expiresAt
      createdAt
    }
  }
`;

export const PREVIEW_WORKSPACE_INVITE_QUERY = gql`
  query PreviewWorkspaceInvite($token: String!) {
    previewWorkspaceInvite(token: $token) {
      workspaceName
      invitedEmail
      invitedByEmail
      expiresAt
    }
  }
`;

// ============ Workspace Mutations ============

export const CREATE_WORKSPACE_MUTATION = gql`
  mutation CreateWorkspace($name: String!) {
    createWorkspace(name: $name) {
      id
      name
      ownerUserId
      createdAt
      updatedAt
      myRole
      memberCount
    }
  }
`;

export const INVITE_TO_WORKSPACE_MUTATION = gql`
  mutation InviteToWorkspace($workspaceId: ID!, $email: String!) {
    inviteToWorkspace(workspaceId: $workspaceId, email: $email) {
      success
      code
      message
      invitation {
        id
        workspaceId
        workspaceName
        invitedEmail
        invitedByEmail
        status
        expiresAt
        createdAt
      }
      token
      inviteLink
    }
  }
`;

export const ACCEPT_WORKSPACE_INVITE_MUTATION = gql`
  mutation AcceptWorkspaceInvite($token: String, $invitationId: ID) {
    acceptWorkspaceInvite(token: $token, invitationId: $invitationId) {
      success
      code
      message
      workspace {
        id
        name
        ownerUserId
        createdAt
        updatedAt
        myRole
        memberCount
      }
    }
  }
`;

export const DECLINE_WORKSPACE_INVITE_MUTATION = gql`
  mutation DeclineWorkspaceInvite($token: String, $invitationId: ID) {
    declineWorkspaceInvite(token: $token, invitationId: $invitationId) {
      success
      message
    }
  }
`;

export const REMOVE_WORKSPACE_MEMBER_MUTATION = gql`
  mutation RemoveWorkspaceMember($workspaceId: ID!, $userId: ID!) {
    removeWorkspaceMember(workspaceId: $workspaceId, userId: $userId) {
      success
      message
    }
  }
`;

export const RESEND_WORKSPACE_INVITE_MUTATION = gql`
  mutation ResendWorkspaceInvite($invitationId: ID!) {
    resendWorkspaceInvite(invitationId: $invitationId) {
      success
      message
    }
  }
`;

// ============ Notification Queries ============

export const MY_NOTIFICATIONS_QUERY = gql`
  query MyNotifications($status: NotificationStatus) {
    myNotifications(status: $status) {
      id
      type
      entityId
      title
      body
      status
      createdAt
      readAt
    }
  }
`;

export const UNREAD_NOTIFICATION_COUNT_QUERY = gql`
  query UnreadNotificationCount {
    unreadNotificationCount
  }
`;

// ============ Notification Mutations ============

export const MARK_NOTIFICATION_READ_MUTATION = gql`
  mutation MarkNotificationRead($notificationId: ID!) {
    markNotificationRead(notificationId: $notificationId) {
      success
      message
    }
  }
`;

export const MARK_ALL_NOTIFICATIONS_READ_MUTATION = gql`
  mutation MarkAllNotificationsRead {
    markAllNotificationsRead {
      success
      message
    }
  }
`;

// ============ Share Request Queries ============

export const SENT_SHARE_REQUESTS_QUERY = gql`
  query SentShareRequests($workspaceId: ID) {
    sentShareRequests(workspaceId: $workspaceId) {
      id
      workspaceId
      itemId
      senderUserId
      senderEmail
      receiverUserId
      receiverEmail
      encryptedItemKey
      itemKeyNonce
      status
      expiresAt
      createdAt
      respondedAt
    }
  }
`;

export const PENDING_SHARE_REQUESTS_QUERY = gql`
  query PendingShareRequests($workspaceId: ID) {
    pendingShareRequests(workspaceId: $workspaceId) {
      id
      workspaceId
      itemId
      senderUserId
      senderEmail
      receiverUserId
      receiverEmail
      encryptedItemKey
      itemKeyNonce
      status
      expiresAt
      createdAt
      respondedAt
      item {
        id
        type
        encryptedPayload
        payloadNonce
        itemKeyEncrypted
        itemKeyNonce
        isFavorite
        createdAt
        updatedAt
        isOwner
        ownerId
        ownerEmail
        ownerPublicKey
      }
    }
  }
`;

// ============ Share Request Mutations ============

export const CREATE_SHARE_REQUEST_MUTATION = gql`
  mutation CreateShareRequest($input: CreateShareRequestInput!) {
    createShareRequest(input: $input) {
      success
      code
      message
      shareRequest {
        id
        workspaceId
        itemId
        senderUserId
        senderEmail
        receiverUserId
        receiverEmail
        status
        expiresAt
        createdAt
      }
    }
  }
`;

export const ACCEPT_SHARE_REQUEST_MUTATION = gql`
  mutation AcceptShareRequest($token: String, $shareRequestId: ID) {
    acceptShareRequest(token: $token, shareRequestId: $shareRequestId) {
      success
      code
      message
    }
  }
`;

export const DECLINE_SHARE_REQUEST_MUTATION = gql`
  mutation DeclineShareRequest($token: String, $shareRequestId: ID) {
    declineShareRequest(token: $token, shareRequestId: $shareRequestId) {
      success
      code
      message
    }
  }
`;

export const CANCEL_SHARE_REQUEST_MUTATION = gql`
  mutation CancelShareRequest($shareRequestId: ID!) {
    cancelShareRequest(shareRequestId: $shareRequestId) {
      success
      message
    }
  }
`;